import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Star, Send, Quote } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Testimonials = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stars, setStars] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    text: "",
    rating: 0
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const generateStars = () => {
      const newStars = [];
      for (let i = 0; i < 80; i++) {
        newStars.push({
          id: i,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          delay: `${Math.random() * 3}s`
        });
      }
      setStars(newStars);
    };
    generateStars();

    // Fetch approved testimonials
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  // Auto-slide carousel
  useEffect(() => {
    if (testimonials.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [testimonials.length]);

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.text.trim() || formData.rating === 0) {
      setSubmitStatus({ type: "error", message: t('testimonials_error') });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/testimonials`, formData);
      setSubmitStatus({ type: "success", message: t('testimonials_success') });
      setFormData({ name: "", text: "", rating: 0 });
    } catch (error) {
      console.error("Error submitting testimonial:", error);
      setSubmitStatus({ type: "error", message: t('testimonials_error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <div className="min-h-screen relative pb-12">
      <div className="stars-container">
        {stars.map((star) => (
          <div
            key={star.id}
            className="star"
            style={{
              left: star.left,
              top: star.top,
              animationDelay: star.delay
            }}
          />
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        <Button
          onClick={() => navigate("/")}
          variant="outline"
          className="mb-8 bg-white/5 hover:bg-white/10 border-white/20 text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back')}
        </Button>

        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Cinzel, serif' }}>
            📖 {t('testimonials_title')}
          </h1>
          <p className="text-lg text-[#F4C430]">{t('testimonials_subtitle')}</p>
        </div>

        {/* Testimonials Carousel */}
        <Card className="glass-card border-2 golden-border mb-12">
          <CardHeader>
            <CardTitle className="text-2xl text-white text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              ✨ {t('testimonials_what_say')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {testimonials.length > 0 ? (
              <div className="relative">
                <div className="min-h-[200px] flex items-center justify-center p-6">
                  <div className="text-center max-w-2xl">
                    <Quote className="w-12 h-12 text-[#F4C430] mx-auto mb-4 opacity-50" />
                    <p className="text-white/90 text-lg mb-4 italic">
                      "{testimonials[currentIndex].text}"
                    </p>
                    <div className="flex justify-center mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < testimonials[currentIndex].rating
                              ? "fill-[#F4C430] text-[#F4C430]"
                              : "text-white/30"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-[#F4C430] font-semibold">— {testimonials[currentIndex].name}</p>
                  </div>
                </div>

                {testimonials.length > 1 && (
                  <div className="flex justify-center items-center gap-4 mt-4">
                    <Button
                      onClick={goToPrevious}
                      variant="outline"
                      className="bg-white/5 hover:bg-white/10 border-[#F4C430]/50 text-white"
                    >
                      ←
                    </Button>
                    <div className="flex gap-2">
                      {testimonials.map((_, index) => (
                        <div
                          key={index}
                          className={`w-2 h-2 rounded-full transition-all ${
                            index === currentIndex ? "bg-[#F4C430] w-6" : "bg-white/30"
                          }`}
                        />
                      ))}
                    </div>
                    <Button
                      onClick={goToNext}
                      variant="outline"
                      className="bg-white/5 hover:bg-white/10 border-[#F4C430]/50 text-white"
                    >
                      →
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-white/60">{t('testimonials_no_testimonials')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Testimonial Form */}
        <Card className="glass-card border-2 golden-border">
          <CardHeader>
            <CardTitle className="text-2xl text-white text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              ✍️ {t('testimonials_form_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-white mb-2 font-semibold">
                  {t('testimonials_name_label')} *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('testimonials_name_placeholder')}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
                  maxLength={100}
                  required
                />
              </div>

              <div>
                <label className="block text-white mb-2 font-semibold">
                  {t('testimonials_text_label')} *
                </label>
                <Textarea
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  placeholder={t('testimonials_text_placeholder')}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 min-h-[120px] resize-none"
                  maxLength={1000}
                  required
                />
                <p className="text-white/40 text-sm mt-1">{formData.text.length}/1000</p>
              </div>

              <div>
                <label className="block text-white mb-2 font-semibold">
                  {t('testimonials_rating_label')} *
                </label>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-10 h-10 ${
                          star <= (hoverRating || formData.rating)
                            ? "fill-[#F4C430] text-[#F4C430]"
                            : "text-white/30"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {submitStatus && (
                <div
                  className={`p-4 rounded-lg ${
                    submitStatus.type === "success"
                      ? "bg-green-500/20 border border-green-500/50 text-green-300"
                      : "bg-red-500/20 border border-red-500/50 text-red-300"
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4C430] hover:from-[#F4C430] hover:to-[#D4AF37] text-[#0a2e38] font-semibold"
              >
                <Send className="mr-2 h-5 w-5" />
                {isSubmitting ? "Envoi..." : t('testimonials_submit_btn')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Testimonials;
